import React from "react";
import { useAudit } from "../hooks/useDashboardData";

const SystemIcon: React.FC<{ type: string }> = ({ type }) => {
  if (type === "database" || type === "snowflake") {
    return (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
        <ellipse cx="11" cy="6" rx="7" ry="3" stroke="white" strokeWidth="1.4" />
        <path d="M4 6v5c0 1.657 3.134 3 7 3s7-1.343 7-3V6" stroke="white" strokeWidth="1.4" />
        <path d="M4 11v5c0 1.657 3.134 3 7 3s7-1.343 7-3v-5" stroke="white" strokeWidth="1.4" />
      </svg>
    );
  }
  if (type === "chart") {
    return (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
        <rect x="3" y="13" width="4" height="6" rx="1" stroke="white" strokeWidth="1.4" />
        <rect x="9" y="9"  width="4" height="10" rx="1" stroke="white" strokeWidth="1.4" />
        <rect x="15" y="5" width="4" height="14" rx="1" stroke="white" strokeWidth="1.4" />
      </svg>
    );
  }
  // gear
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <circle cx="11" cy="11" r="3" stroke="white" strokeWidth="1.4" />
      <path d="M11 3v2M11 17v2M3 11h2M17 11h2M5.636 5.636l1.414 1.414M14.95 14.95l1.414 1.414M5.636 16.364l1.414-1.414M14.95 7.05l1.414-1.414" stroke="white" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
};

const AuditView: React.FC = () => {
  const { data, isLoading, error } = useAudit();

  if (isLoading) {
    return (
      <div className="aud-page">
        <div className="aud-loading">Loading Audit data…</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="aud-page">
        <div className="aud-error">Unable to load audit data.</div>
      </div>
    );
  }

  const { rlsPolicy, auditLog, architecture, footer } = data;

  return (
    <div className="aud-page">
      <div className="aud-scroll-area">

        {/* ── ROW-LEVEL SECURITY ─────────────────────────────────────────── */}
        <section className="aud-section">
          <div className="aud-section-header">
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
              <path d="M7.5 1.5L2 4v4c0 3.2 2.5 5.5 5.5 6.3 3-.8 5.5-3.1 5.5-6.3V4L7.5 1.5z" stroke="#165697" strokeWidth="1.3" strokeLinejoin="round" />
              <rect x="5.5" y="6.5" width="4" height="3" rx="0.5" stroke="#165697" strokeWidth="1.2" />
              <path d="M6.5 6.5V5.5a1 1 0 0 1 2 0v1" stroke="#165697" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
            <span className="aud-section-title">ROW-LEVEL SECURITY (RLS)</span>
          </div>

          <div className="aud-rls-card">
            <div className="aud-rls-icon">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 2L3 5.5v5c0 4 3.1 7.1 7 8 3.9-.9 7-4 7-8v-5L10 2z" stroke="#165697" strokeWidth="1.4" strokeLinejoin="round" />
                <rect x="7" y="9" width="6" height="4.5" rx="0.8" stroke="#165697" strokeWidth="1.2" />
                <path d="M8.5 9V7.5a1.5 1.5 0 0 1 3 0V9" stroke="#165697" strokeWidth="1.2" strokeLinecap="round" />
              </svg>
            </div>
            <div className="aud-rls-body">
              <div className="aud-rls-title-row">
                <span className="aud-rls-name">{rlsPolicy.name}</span>
                <span className="aud-rls-badge">{rlsPolicy.status}</span>
              </div>
              <p className="aud-rls-desc">
                {rlsPolicy.description.split(rlsPolicy.highlightText).map((part: string, i: number) => (
                  <React.Fragment key={i}>
                    {i > 0 && <a href="#" className="aud-rls-link">{rlsPolicy.highlightText}</a>}
                    {part}
                  </React.Fragment>
                ))}
              </p>
            </div>
          </div>
        </section>

        {/* ── AUDIT LOG ─────────────────────────────────────────────────── */}
        <section className="aud-section">
          <div className="aud-section-header">
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
              <circle cx="7.5" cy="7.5" r="5.5" stroke="#165697" strokeWidth="1.3" />
              <path d="M7.5 4.5v3l2 1.5" stroke="#165697" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="aud-section-title">AUDIT LOG</span>
            <div className="aud-retention-pill">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <circle cx="6" cy="6" r="4.5" stroke="#64748b" strokeWidth="1.1" />
                <path d="M6 3.5v2.5l1.5 1" stroke="#64748b" strokeWidth="1.1" strokeLinecap="round" />
              </svg>
              Retention: {auditLog.retention}
            </div>
          </div>

          <div className="aud-table-wrap">
            <table className="aud-table">
              <thead>
                <tr>
                  <th className="aud-th">USER</th>
                  <th className="aud-th">ACTION</th>
                  <th className="aud-th">TIMESTAMP</th>
                  <th className="aud-th">FILTERS USED</th>
                  <th className="aud-th aud-th--right">DATASET VERSION</th>
                </tr>
              </thead>
              <tbody>
                {auditLog.entries.map((entry: any, i: number) => (
                  <tr key={i} className="aud-tr">
                    <td className="aud-td">
                      <div className="aud-user-cell">
                        <div className="aud-avatar" style={{ background: entry.color }}>
                          {entry.initials}
                        </div>
                        <span className="aud-username">{entry.user}</span>
                      </div>
                    </td>
                    <td className="aud-td">
                      <div className="aud-action-cell">
                        {entry.hasDot && <span className="aud-dot" />}
                        <span className={entry.hasDot ? "" : "aud-action-plain"}>{entry.action}</span>
                      </div>
                    </td>
                    <td className="aud-td aud-timestamp">{entry.timestamp}</td>
                    <td className="aud-td">
                      <div className="aud-filters-cell">
                        {entry.filters.map((f: string, j: number) => (
                          <span key={j} className="aud-filter-pill">{f}</span>
                        ))}
                      </div>
                    </td>
                    <td className="aud-td aud-td--right">
                      <span className="aud-version-badge">{entry.version}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* ── ARCHITECTURE & LINEAGE ────────────────────────────────────── */}
        <section className="aud-section">
          <div className="aud-section-header">
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
              <rect x="1" y="4" width="4" height="3" rx="0.8" stroke="#165697" strokeWidth="1.2" />
              <rect x="5.5" y="1" width="4" height="3" rx="0.8" stroke="#165697" strokeWidth="1.2" />
              <rect x="10" y="4" width="4" height="3" rx="0.8" stroke="#165697" strokeWidth="1.2" />
              <rect x="5.5" y="11" width="4" height="3" rx="0.8" stroke="#165697" strokeWidth="1.2" />
              <path d="M5 5.5H3M7.5 4V3M10 5.5h2M7.5 7v4" stroke="#165697" strokeWidth="1.1" strokeLinecap="round" />
            </svg>
            <span className="aud-section-title">ARCHITECTURE &amp; LINEAGE</span>
          </div>

          <div className="aud-arch-card">
            <div className="aud-arch-subheader">
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                <path d="M2 6.5h9M8 3.5l3 3-3 3" stroke="#165697" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span className="aud-arch-subheader-text">LIVE DATA FLOW PROTOCOL</span>
            </div>

            <div className="aud-arch-systems">
              {architecture.liveDataFlow.map((sys: any, i: number) => (
                <React.Fragment key={sys.name}>
                  <div className="aud-system-card">
                    <div className="aud-system-icon">
                      <SystemIcon type={sys.icon} />
                    </div>
                    <div className="aud-system-name">{sys.name}</div>
                    <div className="aud-system-role">{sys.role}</div>
                  </div>
                  {i < architecture.liveDataFlow.length - 1 && (
                    <div className="aud-flow-arrow">
                      <svg width="20" height="14" viewBox="0 0 20 14" fill="none">
                        <path d="M1 7h16M13 2l6 5-6 5" stroke="#CBD5E1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>

            <div className="aud-constraint-banner">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M7 1.5L1.5 4v4c0 2.8 2.2 4.9 5.5 5.6 3.3-.7 5.5-2.8 5.5-5.6V4L7 1.5z" stroke="#DC2626" strokeWidth="1.2" strokeLinejoin="round" />
                <rect x="4.5" y="6.5" width="5" height="3.5" rx="0.6" stroke="#DC2626" strokeWidth="1.1" />
                <path d="M5.5 6.5V5.5a1.5 1.5 0 0 1 3 0v1" stroke="#DC2626" strokeWidth="1.1" strokeLinecap="round" />
              </svg>
              <span>{architecture.constraint}</span>
            </div>

            <p className="aud-arch-note">{architecture.note}</p>
          </div>
        </section>
      </div>

      {/* ── Footer ────────────────────────────────────────────────────── */}
      <footer className="aud-footer">
        <div className="aud-footer-left">
          <span>SYSTEM STATUS: {footer.systemStatus}</span>
          <span className="aud-footer-sep" />
          <span>ENCRYPTION: {footer.encryption}</span>
        </div>
        <div className="aud-footer-center">
          <a href="#" className="aud-footer-link">Help Center</a>
          <a href="#" className="aud-footer-link">Privacy Policy</a>
        </div>
        <div className="aud-footer-right">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <circle cx="7" cy="7" r="5.5" stroke="#165697" strokeWidth="1.3" />
            <path d="M4.5 7l1.8 1.8 3.2-3.2" stroke="#165697" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span className="aud-footer-compliance">COMPLIANCE VERIFIED</span>
        </div>
      </footer>
    </div>
  );
};

export default AuditView;
